import { fetchServerTime } from "@/api/modules/system";
import { makeUtcDate } from "@/utilities/time";

function makeCorrectSelectedInterval(interval, startMinute) {
    // Determine how many minutes we should add to the date so the minute number matches the given interval
    let leftOvers = startMinute !== 0 ? interval - (startMinute % interval) : 0;
    return interval === leftOvers ? 0 : leftOvers;
}

export const state = {
    /**
     * LAST invalid date before a VALID period begins
     */
    selectableDatesStart: null,
    /**
     * FIRST invalid date after a VALID period ends
     */
    selectableDatesEnd: null,
    /**
     * Checks if the selected departure date is valid in between the valid dates in the product valid period
     */
    isSelectedDepartureDateValid: true,
    /**
     * The server current time
     */
    serverDateTime: null,
    /**
     * The selected DATE for departure
     */
    selectedDepartureDate: null,
    /**
     * The dynamic created time selections based on a time interval in minutes. Changes when the date changes
     */
    timeSpans: null,
    /**
     * The minute interval for the time selector
     */
    spanInterval: 1,
    /**
     * Used to tell if user did not select a timespan for travel
     */
    leaveNow: false,
    /**
     * Which time target are we search, departure or arrival
     */
    selectedSearchType: "Departures"
};

export const mutations = {
    forceDateUpdate: state => {
        const tmp = state.selectedDepartureDate;

        state.selectedDepartureDate = new Date();

        state.selectedDepartureDate = tmp;
    },
    /**
     * Update the selected departure date
     */
    updateDepartureDate: (state, payload) => {
        state.selectedDepartureDate = makeUtcDate(
            payload.departureDate
        ).valueOf();
    },
    /**
     * Update the current time according to server time
     */
    updateServerTime: (state, payload) => {
        state.serverDateTime = makeUtcDate(payload.serverTime).valueOf();
        state.serverTicketTime = makeUtcDate(payload.ticketTime).valueOf();
    },
    /**
     * Update the calendar with valid dates based on the selected product
     */
    updateSelectableDatesInCalendar: (state, payload) => {
        state.selectableDatesStart = makeUtcDate(payload.periodStart).valueOf();
        state.selectableDatesEnd =
            payload.periodEnd === null
                ? null
                : makeUtcDate(payload.periodEnd).valueOf();
    },
    /**
     * Update a bool indicating if a valid date is selected
     */
    updateIsSelectedDepartureDateValid: (state, valid) => {
        state.isSelectedDepartureDateValid = valid;
    },
    /**
     * Update the available time spans
     */
    updateTimeSpans: (state, payload) => {
        state.timeSpans = payload.spans;
    },
    /**
     * Update the time span interval
     */
    updateInterval: (state, payload) => {
        state.spanInterval = payload.interval;
    },
    /**
     * Update leave now setting
     */
    updateLeaveNow: (state, payload) => {
        state.leaveNow = payload.leaveNow;
    },
    /**
     * Update search type
     */
    updateSearchType: (state, payload) => {
        state.selectedSearchType = payload;
    }
};

export const getters = {
    /**
     * RETURNS THE CURRENTLY SELECTED DEPARTURE DATE
     */
    getDepartureDateAsMoment: state => {
        let dateAndTime =
            state.selectedDepartureDate !== null
                ? makeUtcDate(state.selectedDepartureDate).second(0)
                : null;

        return dateAndTime === null ? null : dateAndTime;
    },
    /**
     * RETURNS THE CURRENTLY SELECTED DEPARTURE DATE
     * BUT THE MINUTE CLOCK IS MODIFIED TO MATCH THE INTERVAL SPANS
     */
    getModifiedDepartureDateAsMoment: state => {
        let dateAndTime =
            state.selectedDepartureDate !== null
                ? makeUtcDate(state.selectedDepartureDate).second(0)
                : null;
        if (dateAndTime !== null) {
            let leftOvers = makeCorrectSelectedInterval(
                state.spanInterval,
                dateAndTime.minute()
            );
            dateAndTime = makeUtcDate(dateAndTime).add(leftOvers, "minute");
        }

        return dateAndTime;
    },
    /**
     * RETURNS THE CURRENTLY SELECTABLE DATES BASED ON SELECTED PRODUCT
     */
    getSelectableDates: state => {
        return {
            periodStart: makeUtcDate(state.selectableDatesStart),
            periodEnd:
                state.selectableDatesEnd !== null
                    ? makeUtcDate(state.selectableDatesEnd)
                    : null
        };
    },
    /**
     * RETURNS THE CURRENT SERVER DATETIME
     */
    getServerDateAsMoment: state => {
        return state.serverDateTime !== null
            ? makeUtcDate(state.serverDateTime).second(0)
            : null;
    },
    /**
     * RETURNS TRUE IF THE SELECTED DATE IS WITHIN THE SELECTABLE DATES IN THE CALENDAR
     */
    isSelectedDepartureDateValid: state => {
        return state.isSelectedDepartureDateValid;
    },
    /**
     * RETURNS THE AWAILABLE TIME SPANS FOR THE SELECTED DEPARTURE DATE
     */
    getTimeSpans: state => {
        return state.timeSpans;
    },
    /**
     * RETURNS TRUE IF THE SELECTED DEPARTURE DATE IS TODAY
     * COMPARED WITH THE SERVERTIME
     */
    isDepartureDateToday: state => {
        let isToday = false;
        if (
            state.selectedDepartureDate !== null &&
            state.serverDateTime !== null
        ) {
            isToday = makeUtcDate(state.selectedDepartureDate).isSame(
                makeUtcDate(state.serverDateTime),
                "day"
            );
        }

        return isToday;
    },
    /**
     * RETURNS TRUE IF THE SELECTED DEPARTURE DATE IS TODAY AND WITHIN THE VALID DATE RANGE
     */
    isLeaveNowOptionAwailable: (state, getters) => {
        return (
            getters.isSelectedDepartureDateValid &&
            getters.getSelectableDates.periodStart.isSameOrBefore(
                getters.getServerDateAsMoment,
                "days"
            )
        );
    },
    /***
     * RETURNS THE INTERVAL VALUE
     */
    getInterval: state => {
        return state.spanInterval;
    },
    /**
     * RETURNS THE "LEAVE NOW" BOOLEAN
     */
    getLeaveNow: state => {
        return state.leaveNow;
    },
    /**
     * RETURNS THE SELECTED SEARCHTYPE
     */
    getSelectedSearchType: state => state.selectedSearchType
};

export const actions = {
    /**
     * CREATES THE TIME SPANS THAT SHOULD BE AWAILABLE FOR THE SELECTED DEPARTURE DATE
     */
    setTimeSpans: context => {
        let spanss = [];

        let totalMinutes = 24 * 60;
        let date = makeUtcDate(
            context.getters.getDepartureDateAsMoment
        ).startOf("day");
        let totalSpans = totalMinutes / context.getters.getInterval;

        if (context.getters.isDepartureDateToday) {
            // Set the start time according to server time
            let startHour = context.getters.getServerDateAsMoment.hour();
            let startMinute = context.getters.getServerDateAsMoment.minute();

            // Determine how many minutes we should add to the date so the minute number matches the given interval
            let leftOvers = makeCorrectSelectedInterval(
                context.getters.getInterval,
                startMinute
            );

            // Determine how many spans is needed for the selected date and the given interval
            totalMinutes = (24 - startHour) * 60;

            // If the selected date is today we should remove some spans acording til the first valid minute span
            let removeSpans =
                (startMinute + leftOvers) / context.getters.getInterval;

            // Set the date
            date = makeUtcDate(context.getters.getServerDateAsMoment)
                .add(leftOvers, "minute")
                .second(0);

            // Set the spans
            totalSpans =
                totalMinutes / context.getters.getInterval -
                Math.floor(removeSpans);
        }
        for (let i = 0; i < totalSpans; i++) {
            let m = makeUtcDate(date).add(
                i * context.getters.getInterval,
                "minute"
            );
            spanss.push({
                id: m.valueOf(),
                title: m.format("HH:mm")
            });
        }
        context.commit("updateTimeSpans", {
            spans: spanss
        });
    },
    prepareSelectableDates: (context, { periodStart, periodEnd }) => {
        if (!periodStart)
            throw new Error(
                "cannot prepare selectable dates, periodStart is missing"
            );
        context.commit("updateSelectableDatesInCalendar", {
            periodStart,
            periodEnd
        });
        context.dispatch("determineIfSelectedDepartureDateIsValid");
    },
    /**
     * DOES A CHECK TO SEE IF SELECTED DEPARTURE DATE IS VALID WITHIN THE SELECTABLE DATES IN THE CALENDAR
     */
    determineIfSelectedDepartureDateIsValid: context => {
        // GET DEPARTURE DATE AND TIME
        const departureTime = context.getters.getModifiedDepartureDateAsMoment;

        // IS TODAY
        const today = context.getters.isDepartureDateToday;
        // GET SERVER TIME
        const servertime = context.getters.getServerDateAsMoment;
        // GET THE SELECTABLE DATES PERIOD
        const validPeriod = context.getters.getSelectableDates;

        const start = validPeriod.periodStart; // Can be null
        const end = validPeriod.periodEnd; // Can be null

        // Check if leave now is valid (only on today)
        if (!today) {
            context.commit("updateLeaveNow", false);
        }

        if (
            start !== null &&
            end !== null &&
            start.isSameOrBefore(departureTime) &&
            end.isSameOrAfter(departureTime)
        ) {
            // Departure time is within the range
            // Check if today
            if (today && departureTime.isBefore(servertime)) {
                // Departure time is today but the timestamp is in the past.
                context.commit("updateIsSelectedDepartureDateValid", false);
            } else {
                context.commit("updateIsSelectedDepartureDateValid", true);
            }
        } else if (
            start === null &&
            end !== null &&
            departureTime.isSameOrAfter(servertime) &&
            departureTime.isSameOrBefore(end)
        ) {
            // Departure time is AFTER servertime and BEFORE end period
            context.commit("updateIsSelectedDepartureDateValid", true);
        } else if (
            start !== null &&
            end === null &&
            departureTime.isSameOrAfter(start)
        ) {
            // Departure time is AFTER start and there is no upper limit
            context.commit("updateIsSelectedDepartureDateValid", true);
        } else if (
            start === null &&
            end === null &&
            departureTime.isSameOrAfter(servertime)
        ) {
            // No range and selected time is AFTER server time
            context.commit("updateIsSelectedDepartureDateValid", true);
        } else {
            context.commit("updateIsSelectedDepartureDateValid", false);
        }
    },
    /**
     * SETS THE DEPARTURE DATE WHENEVER A NEW DATE IS SELECTED OR A NEW TIME
     */
    setDepartureDate: (context, payload) => {
        // We change the selected date
        context.commit("updateDepartureDate", {
            departureDate: makeUtcDate(payload.departureDate)
        });
        // DETERMINE IF THE CURRENTLY SELECTED DATE IS STILL VALID
        context.dispatch("determineIfSelectedDepartureDateIsValid");
        // Set servertime
        context.dispatch("setServerTime");
    },
    /**
     * SETS THE SERVER TIME
     */
    setServerTime: (context, payload = null) => {
        return fetchServerTime().then(response => {
            if (!response.ok) {
                throw new Error("Error when fetching server time");
            }

            let serverTime = makeUtcDate(response.responseBody.utcTime);

            context.commit("updateServerTime", {
                serverTime
            });

            if (payload !== null && payload.setDepartureDate) {
                const currentDepartureDate =
                    context.getters.getDepartureDateAsMoment;
                if (
                    payload.force ||
                    !currentDepartureDate ||
                    currentDepartureDate.isBefore(serverTime)
                ) {
                    context.commit("updateDepartureDate", {
                        departureDate: serverTime
                    });
                    context.dispatch("determineIfSelectedDepartureDateIsValid");
                }
            }
        });
    },
    /**
     * SETS THE INTERVAL NUMBER. DEFAULTS TO 1 IF NO VALUE IS PRESENT
     */
    setDateInterval: (context, payload) => {
        context.commit("updateInterval", {
            interval: payload === null ? 1 : payload.interval
        });

        context.dispatch("setTimeSpans");
    },
    /**
     * SETS THE LEAVE NOW VALUE
     */
    setLeaveNow: (context, payload) => {
        // If We would leave now. we should set the departure time to server time
        if (payload !== null && payload.leaveNow) {
            context.dispatch("setServerTime", {
                setDepartureDate: true,
                force: true
            });
        }

        context.commit("updateLeaveNow", {
            leaveNow: payload === null ? true : payload.leaveNow
        });

        context.dispatch("setTimeSpans");
    },
    /**
     * Helper for validating a trip departureInstant
     */
    validateTripDepartureInstant: (context, departureInstant) => {
        return context.dispatch("setServerTime").then(() => {
            const serverTime = context.getters.getServerDateAsMoment;
            const departureUtc = makeUtcDate(departureInstant);
            const threshold =
                context.rootGetters[
                    "system/getDepartureInstantThresholdMinutes"
                ];
            departureUtc.subtract(threshold, "minute");

            if (serverTime.isSameOrAfter(departureUtc)) {
                throw new Error("InvalidTripDepartureInstant");
            }

            return true;
        });
    }
};
